import { groupLabelCode } from "@bt/domain";
import { useMutation } from "@tanstack/react-query";
import { CheckCheckIcon, CircleCheckIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/empty-state";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/misc";
import { useTRPC } from "@/lib/trpc";
import { type LabelItem, LabelRow, useShowError } from "./shared";

/**
 * Labels › Not yet confirmed: printed part labels waiting to be scanned on their shelf, in print
 * order, so a phone doubles as the list of which sticker goes where.
 */
export function PendingTab({ labels }: { labels: LabelItem[] | undefined }) {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const showError = useShowError();
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [discarding, setDiscarding] = useState<string | null>(null);
  const confirmAll = useMutation({
    ...trpc.labels.confirmAll.mutationOptions(),
    onSuccess: (count) => toast.success(t("labels.confirmedAll", { count })),
    onError: showError,
  });
  const retire = useMutation({ ...trpc.labels.retire.mutationOptions(), onError: showError });
  if (labels === undefined) return <Skeleton className="h-40 rounded-xl" />;
  if (labels.length === 0) {
    return (
      <EmptyState
        icon={CircleCheckIcon}
        title={t("labels.pendingEmpty")}
        body={t("labels.pendingEmptyBody")}
      />
    );
  }
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">{t("labels.pendingHint")}</p>
        <Button variant="outline" className="shrink-0" onClick={() => setConfirmingAll(true)}>
          <CheckCheckIcon />
          {t("labels.confirmAll")}
        </Button>
      </div>
      <Card className="divide-y divide-border">
        {labels.map((label, i) => (
          <LabelRow
            key={label.code}
            label={label}
            position={i + 1}
            actions={
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("labels.discardCode", { code: groupLabelCode(label.code) })}
                onClick={() => setDiscarding(label.code)}
              >
                <Trash2Icon />
              </Button>
            }
          />
        ))}
      </Card>
      <ConfirmDialog
        open={confirmingAll}
        onOpenChange={setConfirmingAll}
        title={t("labels.confirmAllTitle", { count: labels.length })}
        description={t("labels.confirmAllBody")}
        confirmLabel={t("labels.confirmAll")}
        pending={confirmAll.isPending}
        onConfirm={() => confirmAll.mutate(undefined, { onSuccess: () => setConfirmingAll(false) })}
      />
      <ConfirmDialog
        open={discarding !== null}
        onOpenChange={(open) => !open && setDiscarding(null)}
        title={t("labels.discardTitle", { code: discarding ? groupLabelCode(discarding) : "" })}
        description={t("labels.discardLostBody")}
        confirmLabel={t("labels.discard")}
        destructive
        pending={retire.isPending}
        onConfirm={() =>
          discarding &&
          retire.mutate({ code: discarding }, { onSuccess: () => setDiscarding(null) })
        }
      />
    </div>
  );
}
